import React from "react";
import {
  CalendarOutlined,
  LinkOutlined,
  PaperClipOutlined,
  RobotOutlined,
  TagOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Badge, Card, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";

import { SupportRequestStatus, type SupportRequestTriage } from "@fitness-app/data-models";

import {
  CLOSED_SUPPORT_STATUSES,
  SOURCE_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
  formatPersonName,
  parseFixedInVersions,
  releaseLabel,
  type SupportRequestWithRelations,
} from "../supportConstants";

// Standalone antd status dot (no children, so it doesn't wrap the title in Badge's
// positioning spans).
const ExclamationBadge = () => <Badge status="error" className="mr-1.5" aria-label="Nowe powiadomienie" />;

// Red = overdue, orange = due today/tomorrow, default = further out. Closed requests
// keep a neutral chip — the deadline no longer matters.
const dueTagColor = (dueDate: string, status: string) => {
  if (CLOSED_SUPPORT_STATUSES.includes(status as SupportRequestStatus)) return "default";
  const due = dayjs(dueDate).startOf("day");
  const today = dayjs().startOf("day");
  if (due.isBefore(today)) return "red";
  if (due.diff(today, "day") <= 1) return "orange";
  return "default";
};

interface SupportRequestCardProps {
  request: SupportRequestWithRelations;
  // Unread assigned/mention/deadline notification for the current user (TRE-1547) —
  // cleared by opening the drawer.
  hasUnread?: boolean;
  // Off on the board, where the column already tells the status.
  showStatus?: boolean;
  onClick?: () => void;
}

export const SupportRequestCard: React.FC<SupportRequestCardProps> = ({ request, hasUnread, showStatus, onClick }) => {
  const attachments = Array.isArray(request.attachments) ? request.attachments : [];
  const tags = Array.isArray(request.tags)
    ? (request.tags as unknown[]).filter((tag): tag is string => typeof tag === "string")
    : [];
  const fixedInVersions = parseFixedInVersions(request.fixedInVersions);
  const triage = (request.triage as SupportRequestTriage | null) || null;
  const pendingTriage = triage && !triage.resolution ? triage : null;
  const clientLabel = request.trainee
    ? [request.trainee.firstName, request.trainee.lastName].filter(Boolean).join(" ") || request.trainee.email
    : request.clientEmail;

  return (
    <Card size="small" hoverable onClick={onClick} className="shadow-sm">
      <div className="space-y-2">
        <Typography.Text strong className="block leading-snug">
          {hasUnread && <ExclamationBadge />}
          <span className="mr-1 font-normal text-gray-400">#{request.displayId}</span>
          {request.title}
        </Typography.Text>

        {clientLabel && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <UserOutlined />
            <span className="truncate">{clientLabel}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-1">
          {showStatus && (
            <Tag color={STATUS_COLORS[request.status as SupportRequestStatus] || "default"} className="m-0">
              {request.status === SupportRequestStatus.Reported
                ? // The board column label ("Zgłoszenia") reads redundantly on a card
                  // that already sits under a "Zgłoszenia" tab.
                  "Nowe"
                : STATUS_LABELS[request.status as SupportRequestStatus] || request.status}
            </Tag>
          )}
          {request.linearIssueIdentifier && (
            <Tag icon={<LinkOutlined />} color="cyan" className="m-0">
              {request.linearIssueIdentifier}
            </Tag>
          )}
          {request.source !== "admin" && <Tag className="m-0">{SOURCE_LABELS[request.source] || request.source}</Tag>}
          {tags.map((tag) => (
            <Tag key={tag} color="blue" className="m-0">
              {tag}
            </Tag>
          ))}
          {fixedInVersions.map((entry) => (
            <Tooltip key={`${entry.platform}:${entry.version}`} title="Poprawione w wersji">
              <Tag icon={<TagOutlined />} color="green" className="m-0">
                {releaseLabel(entry.platform, entry.version)}
              </Tag>
            </Tooltip>
          ))}
          {pendingTriage && (
            <Tooltip title={pendingTriage.summary}>
              <Tag
                icon={<RobotOutlined />}
                color={pendingTriage.proposedStatus === SupportRequestStatus.NeedsInfo ? "orange" : "geekblue"}
                className="m-0"
              >
                {pendingTriage.proposedStatus === SupportRequestStatus.NeedsInfo ? "Pytania agenta" : "Diagnoza"}
              </Tag>
            </Tooltip>
          )}
          {attachments.length > 0 && (
            <Tag icon={<PaperClipOutlined />} className="m-0">
              {attachments.length}
            </Tag>
          )}
          {request.dueDate && (
            <Tag icon={<CalendarOutlined />} color={dueTagColor(request.dueDate, request.status)} className="m-0">
              {dayjs(request.dueDate).format("DD.MM")}
            </Tag>
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-gray-400">
          <Tooltip title={formatPersonName(request.reporter) || "Nieznany zgłaszający"}>
            <span className="truncate">{formatPersonName(request.reporter) || "—"}</span>
          </Tooltip>
          <span className="flex items-center gap-1.5">
            {dayjs(request.createdAt).format("DD.MM.YYYY")}
            {request.assignee && (
              <Tooltip title={`Przypisany: ${formatPersonName(request.assignee) || ""}`}>
                <Avatar size={20} src={request.assignee.avatarUrl || undefined}>
                  {(request.assignee.firstName || request.assignee.email || "?").charAt(0).toUpperCase()}
                </Avatar>
              </Tooltip>
            )}
          </span>
        </div>
      </div>
    </Card>
  );
};
